<template>
  <div id="mapContainer" :style="{ height: mapContainerHeight }" class="sidebar-map">
    <l-map
      ref="map"
      :zoom="zoom"
      :center="center"
      :min-zoom="minZoom"
      :max-zoom="maxZoom"
      :options="{ zoomControl: false, attributionControl: false }"
      @update:zoom="zoomUpdated"
    >
      <tile-layer :baseUrl="baseUrl" :token="token" name="tileLayer" layerType="base" />
      <text-layer
        v-if="!!baseTextLayerUrl"
        :baseUrl="baseTextLayerUrl"
        name="textLayer"
        layerType="base"
        maxZoom="19"
        minZoom="0"
        layers="xmap-silkysand-fg"
        format="image/png"
        pane="tilePane"
      />

      <m-marker
        v-for="(marker, index) in markers"
        :key="index"
        :latLng="marker.latLng"
        :icon="marker.icon"
        :color="marker.color"
        :tooltip="marker.tooltip"
        :displayed-number="!marker.number ? 0 : marker.number"
      />
      <clustered-markers :markers="markersClustered" v-if="markersClustered.length" />
      <l-feature-group :ref="'featureGroup' + index" v-for="(item, index) in polylines" :key="index + 'marker'">
        <l-polyline
          :lat-lngs="item"
          :visible="item.visible"
          :color="tracksColors[index % tracksColors.length]"
          fill-color="none"
          @click="$emit('click:polyline', index)"
          :weight="getPolylineWeight(index)"
        />
      </l-feature-group>

      <m-animated-marker
        v-for="(animatedMarker, index) in animatedMarkers"
        ref="animatedMarkers"
        :key="index + 'animatiedMarker'"
        :route="animatedMarker.route"
        :icon="animatedMarker.icon"
        :options="animatedMarker.options"
        :map="mapObject"
      />
      <l-control-attribution position="bottomright" :prefix="eurakaMapsCopyright"></l-control-attribution>
      <l-control-zoom :position="zoomPosition"></l-control-zoom>
      <l-control :position="zoomPosition" class="leaflet-control-zoom leaflet-bar" v-if="canDispalyFitToBoundsBtn">
        <a @click="fitTobounds" class="leaflet-control-zoom-in"><slot name="fitToBoundsContent"></slot></a>
      </l-control>
    </l-map>
  </div>
</template>
<script>
/* eslint-disable vue/no-unused-components */
/* eslint-disable no-undef */
import 'leaflet';
import 'sidebar-v2/js/leaflet-sidebar';
import 'leaflet-polylinedecorator/dist/leaflet.polylineDecorator';
import {
  LMap,
  LTileLayer,
  LMarker,
  LPolyline,
  LControlLayers,
  LFeatureGroup,
  LControlZoom,
  LControl,
  LControlAttribution,
} from 'vue2-leaflet';
import { PruneCluster } from 'exports-loader?exports=PruneCluster!prunecluster/dist/PruneCluster.js';
import { mapState } from 'vuex';

import { leaflet as business } from '@/business';
import LWMSNonTiledLayerComponent from './layers/LWMSNonTiledLayer.vue';
import LWMSTileLayerComponent from './layers/LWMSTileLayer.vue';
import Marker from './marker/index.vue';
import AnimatedMarker from './animated-marker/index.vue';
import ClusteredMarkers from './clustered-markers/index.vue';

export default {
  name: 'eureka-maps',
  props: {
    tracks: {
      type: Array,
    },
    markers: {
      type: Array,
    },
    animatedMarkers: {
      type: Array,
    },
    mapContainerHeight: {
      type: String,
      default: '290px',
    },
    displaySidebar: {
      type: Boolean,
      default: false,
    },
    sidebarPosition: {
      type: String,
      default: 'right',
      validator(value) {
        return value === 'right' || value === 'left';
      },
    },
    center: {
      type: Object,
      default: () => L.latLng(47.41322, -1.219482),
    },
    minZoom: {
      type: Number,
      default: null,
    },
    maxZoom: {
      type: Number,
      default: null,
    },
    colorsTracks: {
      type: Array,
      default: () => [],
    },
    markersClustered: {
      type: Array,
      default: () => [],
    },
    zoomPosition: {
      type: String,
      default: 'topleft',
    },
    polylineSelected: {
      type: Object,
      default: () => ({
        index: null,
        weight: 3,
      }),
    },
    showFitToBounds: {
      type: Boolean,
      default: true,
    },
  },

  mounted() {
    this.mapObject = this.$refs.map.mapObject;
    if (this.displaySidebar) {
      this.initSideBar();
    }
    this.$emit('map-is-ready');
  },

  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    LControlLayers,
    LFeatureGroup,
    LControlZoom,
    LControl,
    LControlAttribution,
    'tile-layer': LWMSTileLayerComponent,
    'text-layer': LWMSNonTiledLayerComponent,
    'm-marker': Marker,
    'm-animated-marker': AnimatedMarker,
    ClusteredMarkers,
  },
  data() {
    //map background xmap-gravelpit-bg, xmap-silkysand-bg xmap-sandbox-bg
    return {
      mapObject: null,
      zoom: 13,
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      defaultPolylineWeight: 3,
      defaultZoom: 13,
    };
  },
  computed: {
    ...mapState('api', {
      eurekaMapsUrl: (state) => state.mapsApiContext.eurekaMapsUrl,
      eurekaMapsRestApiUrl: (state) => state.mapsApiContext.eurekaMapsRestApiUrl,
      eurekaMapsRestApiTilesProviderName: (state) => state.mapsApiContext.eurekaMapsRestApiTilesProviderName,
      eurekaMapsToken: (state) => state.mapsApiContext.token,
      eurakaMapsCopyright: (state) =>
        `${state.mapsApiContext.copyright} <a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>`,
    }),
    mustUseEurekaMapsRestApi() {
      return  !!this.eurekaMapsRestApiUrl && !!this.eurekaMapsRestApiTilesProviderName;
    },
    token() {
      return this.mustUseEurekaMapsRestApi ? '' : this.eurekaMapsToken;
    },
    baseUrl() {
      return this.mustUseEurekaMapsRestApi
                    ? `${this.eurekaMapsRestApiUrl}/api/v1.0/raster-tiles/image-tiles/{z}/{x}/{y}?token=${this.eurekaMapsToken}&mapsProvider=${this.eurekaMapsRestApiTilesProviderName}`
                    : `${this.eurekaMapsUrl}/WMS/GetTile/xmap-gravelpit-bg/{x}/{y}/{z}.png`;
    },
    baseTextLayerUrl() {
      return this.mustUseEurekaMapsRestApi ? '' : `${this.eurekaMapsUrl}/WMS/WMS?xtok=${this.eurekaMapsToken}`;
    },
    polylines() {
      return this.tracks;
    },
    tracksColors() {
      return this.colorsTracks && this.colorsTracks.length ? this.colorsTracks : ['#3388ff', 'green'];
    },
    canDispalyFitToBoundsBtn() {
      return (
        this.showFitToBounds &&
        (this.markers?.length || this.markersClustered?.length) &&
        this.defaultZoom !== this.zoom
      );
    },
  },
  methods: {
    getAnimatedMarkersPositions() {
      if (!this.animatedMarkers.length) return [];
      if (!this.$refs.animatedMarkers || !this.$refs.animatedMarkers.length) return [];
      return this.$refs.animatedMarkers.map((ref) => ref.mapObject.getLatLng());
    },
    invalidateSize() {
      this.mapObject.invalidateSize();
    },

    setView(coordinate, zoom) {
      zoom = zoom || this.mapObject.getZoom() || 8;

      this.$refs.map.$nextTick(() => {
        this.mapObject.setView(business.getLatLongFromCoordinates(coordinate), zoom);
        this.defaultZoom = zoom;
      });
    },

    // eslint-disable-next-line no-unused-vars
    flyToBounds(coordinates, options) {
      this.$refs.map.$nextTick(() => {
        this.mapObject.flyToBounds(L.latLngBounds(business.getLatLongFromCoordinates(coordinates)));
        this.defaultZoom = this.mapObject.getZoom();
      });
    },

    fitBounds(coordinates, options, setDefaultZoom = true) {
      options = { ...options, animate: false } || { animate: false };
      this.$refs.map.$nextTick(() => {
        this.mapObject.fitBounds(L.latLngBounds(business.getLatLongFromCoordinates(coordinates)), options);
        if (setDefaultZoom) this.defaultZoom = this.mapObject.getZoom();
      });
    },
    initSideBar() {
      const sidebarPosition = this.sidebarPosition;

      if (sidebarPosition && (sidebarPosition === 'right' || sidebarPosition === 'left')) {
        let sideBar = L.control.sidebar('sidebar', {
          position: sidebarPosition,
        });
        this.mapObject.addControl(sideBar);

        this.mapObject.on('fullscreenchange', () => {
          if (this.mapObject.isFullscreen()) {
            sideBar.classList.add('sidebar-full-screen');
          } else {
            sideBar.classList.remove('sidebar-full-screen');
          }
        });
      } else {
        console.error('The side bar position must be right or left');
      }
    },
    getPruneMarker(latitude, longitude, iconUrl, popup, size, icon, color) {
      let marker = new PruneCluster.Marker(latitude, longitude);
      if (!size) size = [32, 32];
      if (iconUrl) marker.data.icon = L.icon({ iconUrl: iconUrl, iconSize: size });
      if (icon && color) {
        let awsomeMarker = L.AwesomeMarkers.icon({
          icon: icon,
          markerColor: color,
          prefix: 'fa',
        });
        marker.data.icon = awsomeMarker;
      }
      if (popup) marker.data.popup = popup;
      return marker;
    },
    addPruneCluster(pruneCluster) {
      this.addLayer(pruneCluster);
    },
    addLayer(layer) {
      layer.userLayer = true;
      this.mapObject.addLayer(layer);
    },
    cleanMap() {
      this.mapObject.eachLayer((layer) => {
        if (layer.userLayer) this.mapObject.removeLayer(layer);
      });
    },
    removeSpecificLayer(layer) {
      this.mapObject.removeLayer(layer);
    },
    addDragAndDropMarkers(markers, popUp) {
      let _this = this;
      L.Marker.prototype.animateDragging = function () {
        let iconMargin, shadowMargin;

        this.on('dragstart', function () {
          if (!iconMargin) {
            iconMargin = parseInt(L.DomUtil.getStyle(this._icon, 'marginTop'));
            shadowMargin = parseInt(L.DomUtil.getStyle(this._shadow, 'marginLeft'));
          }

          this._icon.style.marginTop = iconMargin - 15 + 'px';
          this._shadow.style.marginLeft = shadowMargin + 8 + 'px';
        });

        return this.on('dragend', function () {
          this._icon.style.marginTop = iconMargin + 'px';
          this._shadow.style.marginLeft = shadowMargin + 'px';
          _this.$emit('dragend');
        });
      };

      let options = {
        draggable: true,
      };

      return L.marker(markers, options).animateDragging().addTo(this.mapObject).bindPopup(popUp).openPopup();
    },
    drawItineraryWithDecorator(coordinates, color, centredMap, addArrows, arrowsColor) {
      if (!coordinates || coordinates.length === 0) {
        throw 'missing coordinates';
      }
      let ret = { polyline: null, decorator: null };
      ret.polyline = this.drawItinerary(coordinates, color, centredMap);
      if (addArrows) {
        ret.decorator = L.polylineDecorator(ret.polyline, {
          patterns: [
            {
              offset: 0,
              repeat: 70,
              symbol: L.Symbol.arrowHead({
                pixelSize: 5,
                pathOptions: { fillOpacity: 2, color: arrowsColor, weight: 2 },
              }),
            },
          ],
        });
        this.addLayer(ret.decorator);
      }
      return ret;
    },
    drawItinerary(coordinates, color, centredMap) {
      if (null === centredMap || undefined === centredMap) {
        centredMap = true;
      }

      if (!coordinates || coordinates.length === 0) {
        throw 'missing coordinates';
      }
      let latLng = business.getLatLongFromCoordinates(coordinates);
      let polyline = new L.Polyline(latLng, { color: color });
      this.addLayer(polyline);
      if (centredMap) this.mapObject.fitBounds(polyline.getBounds());
      return polyline;
    },
    addAnimatedColorMarker(coordinates, options, markerColor, endCallBack) {
      if (!coordinates || coordinates.length === 0) {
        return null;
      }
      let latLng = business.getLatLongFromCoordinates(coordinates);
      let awsomeMarker = L.AwesomeMarkers.icon({
        icon: 'fa-people-carry',
        prefix: 'fa',
        markerColor: markerColor,
      });

      let line = L.polyline(latLng, { color: markerColor });
      let animatedMarker = L.animatedMarker(line.getLatLngs(), {
        onEnd: endCallBack,
        icon: awsomeMarker,
        distance: options.distance,
        interval: options.interval,
        autoStart: options.autoStart,
      });
      this.addLayer(animatedMarker);

      return animatedMarker;
    },

    showFeatureGroupLayer(index, markers) {
      this.$nextTick(() => {
        if (!this.$refs['featureGroup' + index]) return;
        this.$refs['featureGroup' + index][0].mapObject.bringToFront();
        this.fitBounds(markers, null, false);
      });
    },
    getPolylineWeight(index) {
      if (this.polylineSelected.index == null || this.polylineSelected.index !== index)
        return this.defaultPolylineWeight;
      return this.polylineSelected.weight;
    },
    zoomUpdated(zoom) {
      this.$refs.map.$nextTick(() => {
        this.zoom = zoom;
      });
    },
    fitTobounds() {
      this.$refs.map.$nextTick(() => {
        if (this.markers.length) this.fitBounds(this.markers.map((marker) => marker.latLng));
        else if (this.markersClustered.length) this.fitBounds(this.markersClustered.map((marker) => marker.latLng));
      });
    },
  },
  // watch: {
  //   markers: {
  //     immediate: true,
  //     handler(newVal, oldVal) {
  //       this.$nextTick(() => {
  //         //this.flyToBounds(this.markers.map(marker => marker.latLng));
  //       });
  //     },
  //   },
  // },
};
</script>
<style lang="scss">
@import '~leaflet/dist/leaflet.css';
@import '~leaflet.fullscreen/Control.FullScreen.css';
@import '~leaflet.awesome-markers/dist/leaflet.awesome-markers.css';
@import '~sidebar-v2/css/leaflet-sidebar.min.css';
@import '~prunecluster/dist/LeafletStyleSheet.css';

#mapContainer {
  margin: 0;
}

.awesome-marker i {
  font-size: 19px;
  margin-top: 5px;
}

.animated-marker {
  height: 50px;
  width: 50px;
}
</style>
